import { useState } from 'react';
import { motion } from 'framer-motion';
import SectionWrapper from '../components/SectionWrapper';
import AnimatedSection from '../components/AnimatedSection';
import { createTestimonial } from '../service/testimonial';

const initialFormState = {
  name: '',
  designation: '',
  company_name: '',
  company_logo_url: '',
  rating: 0,
  message: '',
};

const initialErrorState = {
  name: '',
  designation: '',
  rating: '',
  message: '',
};

const ratingLabels = ['Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];

const Feedback = () => {
  const [form, setForm] = useState(initialFormState);
  const [errors, setErrors] = useState(initialErrorState);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [submitError, setSubmitError] = useState('');

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
    setSubmitError('');
  };

  const handleRating = (rating) => {
    setForm((prev) => ({ ...prev, rating }));
    setErrors((prev) => ({ ...prev, rating: '' }));
    setSubmitError('');
  };

  const validate = () => {
    const validation = {};

    if (!form.name.trim()) validation.name = 'Name is required.';
    if (!form.designation.trim()) validation.designation = 'Designation is required.';
    if (!form.rating || form.rating < 1) validation.rating = 'Please select a rating.';
    if (!form.message.trim()) validation.message = 'Write your feedback message.';

    return validation;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSuccess('');
    setSubmitError('');

    const validation = validate();
    if (Object.keys(validation).length) {
      setErrors(validation);
      return;
    }

    setSubmitting(true);

    try {
      const response = await createTestimonial({
        name: form.name.trim(),
        designation: form.designation.trim(),
        company_name: form.company_name.trim(),
        company_logo_url: form.company_logo_url.trim() || undefined,
        rating: form.rating,
        message: form.message.trim(),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Unable to submit feedback at this time.');
      }

      setSuccess('Thank you for your feedback! It helps us improve.');
      setForm(initialFormState);
      setErrors(initialErrorState);
    } catch (error) {
      console.error(error);
      setSubmitError('Something went wrong while submitting. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative overflow-hidden pt-20 sm:pt-28">
      <div className="mx-auto max-w-6xl px-6 lg:px-8">
        <AnimatedSection className="flex flex-col items-center text-center">
          <p className="text-sm uppercase tracking-[0.35em] text-gold">Client Feedback</p>
          <h1 className="mt-4 text-4xl font-semibold text-white sm:text-5xl md:text-6xl">Client Feedback</h1>
          <p className="mx-auto mt-4 max-w-2xl text-base text-slate-300 sm:text-lg">
            We value your experience working with Genius KODS.
          </p>
        </AnimatedSection>
      </div>

      <SectionWrapper>
        <AnimatedSection className="mt-12">
          <div className="mx-auto max-w-4xl rounded-[32px] border border-white/10 bg-white/5 p-8 shadow-[0_35px_90px_rgba(0,0,0,0.18)] backdrop-blur-xl transition-all duration-500 sm:p-10">
            <div className="space-y-6">
              <div className="rounded-3xl border border-white/10 bg-slate-950/20 p-6 shadow-[0_20px_60px_rgba(0,0,0,0.18)]">
                <p className="text-sm uppercase tracking-[0.3em] text-gold">Premium feedback experience</p>
                <h2 className="mt-3 text-2xl font-semibold text-white sm:text-3xl">Share your thoughts with our team</h2>
                <p className="mt-3 text-slate-300">
                  Help us refine every client engagement with your honest rating and feedback.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {success && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="rounded-3xl border border-emerald-400/20 bg-emerald-400/10 p-4 text-sm text-emerald-100"
                  >
                    {success}
                  </motion.div>
                )}

                {submitError && (
                  <div className="rounded-3xl border border-red-400/20 bg-red-500/10 p-4 text-sm text-red-100">
                    {submitError}
                  </div>
                )}

                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium text-slate-200">Name *</label>
                    <input
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      className="mt-3 w-full rounded-3xl border border-white/10 bg-white/5 px-4 py-3 text-white outline-none transition duration-300 ease-out focus:border-gold focus:ring-2 focus:ring-gold/20"
                    />
                    {errors.name && <p className="mt-2 text-sm text-red-300">{errors.name}</p>}
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-200">Designation *</label>
                    <input
                      name="designation"
                      value={form.designation}
                      onChange={handleChange}
                      placeholder="Project role or title"
                      className="mt-3 w-full rounded-3xl border border-white/10 bg-white/5 px-4 py-3 text-white outline-none transition duration-300 ease-out focus:border-gold focus:ring-2 focus:ring-gold/20"
                    />
                    {errors.designation && <p className="mt-2 text-sm text-red-300">{errors.designation}</p>}
                  </div>
                </div>

                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium text-slate-200">Company Name</label>
                    <input
                      name="company_name"
                      value={form.company_name}
                      onChange={handleChange}
                      placeholder="Optional"
                      className="mt-3 w-full rounded-3xl border border-white/10 bg-white/5 px-4 py-3 text-white outline-none transition duration-300 ease-out focus:border-gold focus:ring-2 focus:ring-gold/20"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-200">Company Logo URL</label>
                    <input
                      name="company_logo_url"
                      value={form.company_logo_url}
                      onChange={handleChange}
                      placeholder="https://"
                      className="mt-3 w-full rounded-3xl border border-white/10 bg-white/5 px-4 py-3 text-white outline-none transition duration-300 ease-out focus:border-gold focus:ring-2 focus:ring-gold/20"
                    />
                  </div>
                </div>

                <div className="rounded-3xl border border-white/10 bg-white/5 p-6">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-200">Rating *</p>
                      <p className="mt-1 text-sm text-slate-400">Tap to choose a score between 1 and 5 stars.</p>
                    </div>
                    <div className="flex items-center gap-2 rounded-3xl border border-white/10 bg-slate-900/40 px-3 py-2">
                      {Array.from({ length: 5 }, (_, index) => {
                        const value = index + 1;
                        const isActive = form.rating >= value;
                        return (
                          <motion.button
                            key={value}
                            type="button"
                            whileHover={{ y: -2, scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            transition={{ type: 'spring', stiffness: 260, damping: 18 }}
                            onClick={() => handleRating(value)}
                            className={`rounded-full p-2 transition-colors ${
                              isActive ? 'text-gold' : 'text-slate-500 hover:text-gold/80'
                            }`}
                            aria-label={`${value} star rating`}
                          >
                            <svg viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6">
                              <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                            </svg>
                          </motion.button>
                        );
                      })}
                    </div>
                  </div>
                  {errors.rating && <p className="mt-3 text-sm text-red-300">{errors.rating}</p>}
                  {form.rating > 0 && (
                    <p className="mt-4 text-sm text-slate-400">Selected: {ratingLabels[form.rating - 1]}</p>
                  )}
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-200">Feedback Message *</label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows={6}
                    placeholder="Share what worked, what we can improve, or how the collaboration felt."
                    className="mt-3 w-full rounded-3xl border border-white/10 bg-white/5 px-4 py-4 text-white outline-none transition duration-300 ease-out focus:border-gold focus:ring-2 focus:ring-gold/20"
                  />
                  {errors.message && <p className="mt-2 text-sm text-red-300">{errors.message}</p>}
                </div>

                <motion.button
                  type="submit"
                  whileHover={{ scale: submitting ? 1 : 1.02 }}
                  whileTap={{ scale: submitting ? 1 : 0.98 }}
                  disabled={submitting}
                  className="inline-flex items-center justify-center rounded-full bg-gold px-8 py-3 text-sm font-semibold text-slate-950 transition duration-300 hover:shadow-glow disabled:cursor-not-allowed disabled:bg-gold/60"
                >
                  {submitting ? 'Submitting...' : 'Send Feedback'}
                </motion.button>
              </form>
            </div>
          </div>
        </AnimatedSection>
      </SectionWrapper>
    </div>
  );
};

export default Feedback;
